/**
 * Icons transcribed verbatim from the old panel's `src/assets/icons/`. The
 * path data is copied unchanged so the glyphs are pixel-identical; what
 * changed is that hardcoded `stroke`/`fill` values became `currentColor`, so
 * the caller controls enabled/disabled tint through `text-*` instead of the
 * original's nested `& path` style overrides.
 *
 * These are local to the legacy table widget rather than added to `@noor/ui`:
 * they belong to the reproduction, not the design system, and must not become
 * available to new screens.
 */

/** Re-dispatch the courier search. */
export function LegacyReOrderIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M3.33329 10.0002C3.33329 13.6821 6.31806 16.6668 9.99996 16.6668C12.4676 16.6668 14.622 15.3262 15.7747 13.3335M3.33329 10.0002L1.66663 11.6668M3.33329 10.0002L5.41663 11.2502M16.6666 10.0002C16.6666 6.31826 13.6819 3.3335 9.99996 3.3335C7.53235 3.3335 5.37787 4.67416 4.22517 6.66683M16.6666 10.0002L18.3333 8.3335M16.6666 10.0002L14.5833 8.75016"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Duplicate the order into the create form. */
export function LegacyCopyIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M12.5 9.16667C12.5 8.24619 11.7538 7.5 10.8333 7.5H4.16667C3.24619 7.5 2.5 8.24619 2.5 9.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H10.8333C11.7538 17.5 12.5 16.7538 12.5 15.8333V9.16667Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.5 5C17.5 3.61929 16.3807 2.5 15 2.5H8.33333C6.95262 2.5 5.83333 3.61929 5.83333 5C5.83333 5.46024 6.20643 5.83333 6.66667 5.83333C7.1269 5.83333 7.5 5.46024 7.5 5C7.5 4.53976 7.8731 4.16667 8.33333 4.16667H15C15.4602 4.16667 15.8333 4.53976 15.8333 5V11.6667C15.8333 12.1269 15.4602 12.5 15 12.5C14.5398 12.5 14.1667 12.8731 14.1667 13.3333C14.1667 13.7936 14.5398 14.1667 15 14.1667C16.3807 14.1667 17.5 13.0474 17.5 11.6667V5Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Cancel the order. Rendered in the old grid as `MiniCloseIcon withoutBorder`,
 * i.e. the 32×32 glyph with its white plate and border suppressed — only the
 * circled cross remains, which is what is transcribed here.
 */
export function LegacyCancelIcon({ className }: { className?: string }) {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M14.5 17.5L16 16M16 16L17.5 14.5M16 16L14.5 14.5M16 16L17.5 17.5M22.75 16C22.75 19.7279 19.7279 22.75 16 22.75C12.2721 22.75 9.25 19.7279 9.25 16C9.25 12.2721 12.2721 9.25 16 9.25C19.7279 9.25 22.75 12.2721 22.75 16Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Route origin — a filled dot. */
export function LegacyOriginIcon({ className }: { className?: string }) {
  return (
    <svg
      width="13"
      height="12"
      viewBox="0 0 13 12"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <rect x="0.25" width="12" height="12" rx="6" fill="currentColor" />
      <circle cx="6.25" cy="6" r="3" fill="var(--lg-surface)" />
    </svg>
  );
}

/** Route destination — a chequered finish flag. */
export function LegacyFinishIcon({ className }: { className?: string }) {
  return (
    <svg
      width="13"
      height="12"
      viewBox="0 0 13 12"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <g clipPath="url(#legacy-finish-clip)">
        <path
          d="M1.66666 0C2.0815 0 2.41666 0.335156 2.41666 0.75V1.125L4.03384 0.721875C4.92681 0.499219 5.869 0.602344 6.694 1.01484C7.77916 1.55859 9.0565 1.55859 10.1417 1.01484L10.3667 0.902344C10.8495 0.658594 11.4167 1.01016 11.4167 1.54922V8.10469C11.4167 8.41641 11.2221 8.69766 10.9292 8.80781L10.1159 9.1125C9.03306 9.51797 7.82838 9.45469 6.79478 8.93906C5.9065 8.49375 4.88931 8.38359 3.92369 8.625L2.41666 9V11.25C2.41666 11.6648 2.0815 12 1.66666 12C1.25181 12 0.916656 11.6648 0.916656 11.25V9.375V7.82812V1.5V0.75C0.916656 0.335156 1.25181 0 1.66666 0ZM2.41666 4.38516L3.91666 4.05937V5.59453L2.41666 5.92031V7.45312L3.56041 7.16719C3.67994 7.13672 3.79713 7.11094 3.91666 7.08984V5.59219L4.82838 5.39531C5.02291 5.35313 5.21978 5.33672 5.41666 5.34609V3.84609C5.73541 3.85547 6.05416 3.90703 6.36353 3.99609L6.91666 4.15781V5.72109L5.93931 5.43281C5.76822 5.38359 5.59244 5.35313 5.41666 5.34375V7.01719C5.92759 7.06172 6.4315 7.17422 6.91666 7.35469V5.71875L7.44869 5.87578C7.76509 5.96953 8.08853 6.02578 8.41666 6.04922V4.54453C8.23384 4.52578 8.05103 4.49062 7.87291 4.43906L6.91666 4.15781V2.70469C6.61197 2.61563 6.31197 2.49844 6.02134 2.35313C5.82916 2.25703 5.62525 2.18906 5.41666 2.14687V3.84375C5.11197 3.83437 4.80728 3.8625 4.50963 3.92813L3.91666 4.05937V2.29688L2.41666 2.67188V4.38516ZM8.41666 7.86797C8.81041 7.90313 9.21119 7.85156 9.58853 7.70859L9.91666 7.58672V5.90625L9.7315 5.94844C9.30025 6.04922 8.85728 6.08203 8.41666 6.05391V7.86797ZM9.91666 4.36641V2.70703C9.42681 2.85 8.92291 2.92031 8.41666 2.92031V4.54688C8.74244 4.57969 9.07291 4.55859 9.394 4.48594L9.91666 4.36406V4.36641Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="legacy-finish-clip">
          <rect width="12" height="12" fill="white" transform="translate(0.25)" />
        </clipPath>
      </defs>
    </svg>
  );
}

/** Copy-to-clipboard affordance in the ID cell. */
export function LegacySmallCopyIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M12.5 9.16667C12.5 8.24619 11.7538 7.5 10.8333 7.5H4.16667C3.24619 7.5 2.5 8.24619 2.5 9.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H10.8333C11.7538 17.5 12.5 16.7538 12.5 15.8333V9.16667Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.5 5C17.5 3.61929 16.3807 2.5 15 2.5H8.33333C6.95262 2.5 5.83333 3.61929 5.83333 5C5.83333 5.46024 6.20643 5.83333 6.66667 5.83333C7.1269 5.83333 7.5 5.46024 7.5 5C7.5 4.53976 7.8731 4.16667 8.33333 4.16667H15C15.4602 4.16667 15.8333 4.53976 15.8333 5V11.6667C15.8333 12.1269 15.4602 12.5 15 12.5C14.5398 12.5 14.1667 12.8731 14.1667 13.3333C14.1667 13.7936 14.5398 14.1667 15 14.1667C16.3807 14.1667 17.5 13.0474 17.5 11.6667V5Z"
        fill="currentColor"
      />
    </svg>
  );
}
